// Pure rules for turning a pending farm load into a pre-filled berry intake form.
// The receiver still checks and edits every figure: this only saves the typing.
import type { PendingFarmLoad } from '@/hooks/usePendingFarmLoads'

export interface IntakePrefill {
  growerId: string
  variety:  string
  crates:   string
  vehicle:  string
  notes:    string
}

/** How many crates the farm says it sent, or null when it did not say. A load
 *  counted in crates IS that number. A dolly count is NOT a crate count: use the
 *  crate figure the farm also typed, else leave it empty. A wagon count is 1 or
 *  2, so the farm's crate figure is the better guess, falling back to the wagon
 *  count. The receiver counts and corrects it on the form. */
export function loadCrateCount(l: Pick<PendingFarmLoad, 'container_label' | 'container_count' | 'crate_count'>): number | null {
  const label = l.container_label.trim().toUpperCase()
  const typed = l.crate_count != null && l.crate_count > 0 ? l.crate_count : null
  if (label === 'CRATE') return l.container_count
  if (label === 'DOLLY') return typed
  return typed ?? l.container_count
}

/** The farm often sends the crop name ("Blueberries") where a variety belongs.
 *  That is not a variety, so it is dropped and the block's variety is used. */
export function loadVariety(l: Pick<PendingFarmLoad, 'variety'>, commodityName: string | null): string {
  const v = (l.variety ?? '').trim()
  if (!v) return ''
  if (commodityName && v.toUpperCase() === commodityName.trim().toUpperCase()) return ''
  return v
}

export function farmLoadPrefill(l: PendingFarmLoad, commodityName: string | null): IntakePrefill {
  return {
    growerId: l.grower_id,
    variety:  loadVariety(l, commodityName),
    crates:   String(loadCrateCount(l) ?? ''),
    vehicle:  (l.vehicle_ref ?? '').trim().toUpperCase(),
    notes:    (l.notes_for_packhouse ?? '').trim(),
  }
}

/** The block the picker should select for a load. The load names the FARM's
 *  block. The picker lists either that block (read live from the farm) or this
 *  packhouse's mirror of it. Exactly one match is selected; none or several
 *  (ambiguous) select nothing, and the receiver picks. */
export function pickLoadBlock(
  pickerBlocks: { block_id: string }[],
  loadBlockId: string | null,
  mirrorIds: string[],
): string {
  if (!loadBlockId) return ''
  const wanted = new Set([loadBlockId, ...mirrorIds])
  const hits = pickerBlocks.filter(b => wanted.has(b.block_id))
  return hits.length === 1 ? hits[0].block_id : ''
}
